/**
 * useMergePreInvoices - Hook para combinar múltiples prefacturas
 *
 * POST /api/v1/invoices/pre-invoices/merge
 * Requiere al menos 2 prefacturas con estado DRAFT o REVIEW, mismo tipo y mismo equipo.
 */

import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@shared/hooks/use-toast';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { PreInvoicesApiAdapter } from '../adapters/PreInvoicesApiAdapter';

export const useMergePreInvoices = () => {
  const { t } = useTranslation('preInvoicesModule');
  const { toast } = useToast();
  const [isMerging, setIsMerging] = useState(false);

  const mergePreInvoices = useCallback(
    async (preInvoiceIds: number[], onSuccess?: () => void) => {
      if (preInvoiceIds.length < 2) {
        toast({
          title: t('toast.merge_error.title', 'Error al combinar'),
          description: t('toast.merge_error.minRequired', 'Se requieren al menos 2 prefacturas para combinar.'),
          variant: 'destructive',
        });
        return;
      }

      setIsMerging(true);
      try {
        const httpClient = createHttpClient(apiClientWH);
        const adapter = new PreInvoicesApiAdapter(httpClient);
        await adapter.mergePreInvoices(preInvoiceIds);

        toast({
          title: t('toast.merge_success.title', 'Prefacturas combinadas'),
          description: t('toast.merge_success.description', 'Las prefacturas se han combinado correctamente.'),
        });

        onSuccess?.();
      } catch (error: any) {
        const message =
          error?.response?.data?.message ||
          error?.response?.data?.error?.message ||
          error?.message ||
          t('toast.merge_error.unexpected', 'Error inesperado al combinar prefacturas.');

        toast({
          title: t('toast.merge_error.title', 'Error al combinar'),
          description: message,
          variant: 'destructive',
        });
      } finally {
        setIsMerging(false);
      }
    },
    [t, toast]
  );

  return { mergePreInvoices, isMerging };
};
